import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import BrandLogo from '../../components/common/BrandLogo';
import { MdArrowForward, MdArrowBack, MdError, MdEmail, MdCheckCircle } from 'react-icons/md';
import toast from 'react-hot-toast';
import useAuth from '../../hooks/useAuth';
import { useTenant } from '../../context/TenantContext';
import api from '../../services/api';
import useForceDarkTheme from '../../hooks/useForceDarkTheme';

const CLUB_TYPES = [
  { value: 'martial_arts', label: 'Martial Arts (Taekwondo, Karate, BJJ, etc.)' },
  { value: 'gym',          label: 'Gym / Fitness' },
  { value: 'yoga',         label: 'Yoga / Pilates' },
  { value: 'school',       label: 'School / Academy' },
  { value: 'other',        label: 'Other' },
];

const slugify = (str) =>
  str.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 40);

const PasswordStrength = ({ password }) => {
  const checks = [
    { ok: password.length >= 8,   label: '8+ characters' },
    { ok: /[A-Z]/.test(password), label: 'Uppercase letter' },
    { ok: /[0-9]/.test(password), label: 'Number' },
  ];
  const score = checks.filter(c => c.ok).length;
  const color = score === 3 ? 'bg-green-500' : score === 2 ? 'bg-yellow-400' : score === 1 ? 'bg-orange-400' : 'bg-red-400';

  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex gap-1 h-1">
        {[0,1,2].map(i => (
          <div key={i} className={`flex-1 rounded-full transition-colors ${i < score ? color : 'bg-gray-200'}`} />
        ))}
      </div>
      <div className="flex gap-3">
        {checks.map(c => (
          <span key={c.label} className={`text-xs ${c.ok ? 'text-green-600' : 'text-gray-400'}`}>
            {c.ok ? '✓' : '○'} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const STEPS = ['About You', 'Your Club', 'Choose Plan'];

// OTP state machine: idle → sending → sent → verifying → verified
// Any email change resets back to idle.

export default function ClubSignup() {
  useForceDarkTheme();
  const [step,     setStep]     = useState(0);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);

  // Step 0 fields
  const [name,         setName]         = useState('');
  const [email,        setEmail]        = useState('');
  const [phone,        setPhone]        = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [password,     setPassword]     = useState('');
  const [emailAvailable, setEmailAvailable] = useState(null);

  // OTP state
  const [otpState,   setOtpState]   = useState('idle');   // idle|sending|sent|verifying|verified
  const [otpCode,    setOtpCode]    = useState('');
  const [otpError,   setOtpError]   = useState('');
  const [otpProof,   setOtpProof]   = useState(null);     // signed 15-min proof from verify-otp
  const [countdown,  setCountdown]  = useState(0);
  const countdownRef = useRef(null);

  // Step 1 fields
  const [clubName,   setClubName]   = useState('');
  const [slug,       setSlug]       = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null);
  const [clubType,   setClubType]   = useState('martial_arts');
  const [city,       setCity]       = useState('');

  // Step 2
  const [plan, setPlan] = useState('free');

  const navigate           = useNavigate();
  const { login }          = useAuth();
  const { setClubDirectly } = useTenant();
  const slugCheckRef       = useRef(null);
  const emailCheckRef      = useRef(null);

  // Cleanup countdown on unmount
  useEffect(() => () => clearInterval(countdownRef.current), []);

  // Auto-generate slug from club name
  useEffect(() => {
    if (!slugEdited && clubName) setSlug(slugify(clubName));
  }, [clubName, slugEdited]);

  // Debounced slug availability check
  useEffect(() => {
    if (!slug || slug.length < 3) { setSlugStatus(null); return; }
    setSlugStatus('checking');
    clearTimeout(slugCheckRef.current);
    slugCheckRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post('/api/clubs/check-slug', { slug });
        setSlugStatus(data.available ? 'available' : 'taken');
      } catch { setSlugStatus(null); }
    }, 500);
  }, [slug]);

  // Debounced email availability check
  useEffect(() => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) { setEmailAvailable(null); return; }
    clearTimeout(emailCheckRef.current);
    emailCheckRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/api/clubs/check-owner-email', { params: { email } });
        setEmailAvailable(data.available);
      } catch { setEmailAvailable(null); }
    }, 600);
  }, [email]);

  // Reset OTP whenever email changes (so the user must re-verify the new address)
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (otpState !== 'idle') {
      setOtpState('idle');
      setOtpCode('');
      setOtpError('');
      setOtpProof(null);
      setCountdown(0);
      clearInterval(countdownRef.current);
    }
  };

  const startCountdown = () => {
    setCountdown(60);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    setOtpError('');
    setOtpCode('');
    setOtpState('sending');
    try {
      await api.post('/api/auth/send-otp', { email });
      setOtpState('sent');
      startCountdown();
    } catch (err) {
      setOtpState('idle');
      setOtpError(err.response?.data?.message || 'Failed to send code. Please try again.');
    }
  };

  const handleVerifyOtp = useCallback(async (code) => {
    if (otpState === 'verified') return;
    setOtpState('verifying');
    setOtpError('');
    try {
      const { data } = await api.post('/api/auth/verify-otp', { email, code });
      setOtpProof(data?.otpProof || null);
      setOtpState('verified');
      clearInterval(countdownRef.current);
    } catch (err) {
      setOtpState('sent');
      setOtpError(err.response?.data?.message || 'Incorrect code. Please try again.');
    }
  }, [email, otpState]);

  const handleOtpInput = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(digits);
    setOtpError('');
    if (digits.length === 6) handleVerifyOtp(digits);
  };

  const emailIsValid = /\S+@\S+\.\S+/.test(email);
  const showOtpSection = emailIsValid && emailAvailable !== false;

  // Phone helpers
  const normalizePhone = (ph) => ph.trim().replace(/[\s\-]/g, '');
  const PHONE_RE = /^(\+\d{7,14}|\d{7,15})$/;
  const phoneNorm = normalizePhone(phone);
  const phoneErr  = phoneTouched
    ? !phone.trim()           ? 'Phone number is required.'
    : !PHONE_RE.test(phoneNorm) ? 'Enter a valid phone number.'
    : ''
    : '';

  const validateStep = () => {
    if (step === 0) {
      if (!name.trim())                                    return 'Full name is required';
      if (!emailIsValid)                                   return 'Valid email is required';
      if (otpState !== 'verified')                         return 'Please verify your email address first';
      if (!phone.trim())                               return 'Phone number is required.';
      if (!PHONE_RE.test(normalizePhone(phone)))       return 'Enter a valid phone number.';
      if (password.length < 8)                            return 'Password must be at least 8 characters';
    }
    if (step === 1) {
      if (!clubName.trim())        return 'Club name is required';
      if (slug.length < 3)         return 'Club URL must be at least 3 characters';
      if (slugStatus === 'taken')  return 'That club URL is already taken';
      if (!city.trim())            return 'City is required';
    }
    return null;
  };

  const nextStep = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError('');
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/api/clubs/register', {
        name:          clubName,
        slug,
        clubType,
        ownerName:     name,
        ownerEmail:    email,
        ownerPhone:    normalizePhone(phone),
        ownerPassword: password,
        address:       { city },
        country:       'Nepal',
        plan,
        otpProof,
      });

      setClubDirectly({ id: data.club._id, name: data.club.name, slug: data.club.slug, clubType: data.club.clubType, primaryColor: data.club.primaryColor }, data.club.slug);
      login(
        { id: data.user.id, name: data.user.name, email: data.user.email, role: data.user.role, clubId: data.user.clubId, clubSlug: data.club.slug },
        data.token
      );
      toast.success(`Welcome to TrainAxis! Your 14-day trial has started.`);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'EMAIL_TAKEN') {
        setError('This email is already registered. Sign in instead or use a different email.');
        setStep(0);
      } else if (code === 'SLUG_TAKEN') {
        setError('That club URL is already taken. Please choose a different one.');
        setStep(1);
      } else {
        setError(err.response?.data?.message || 'Something went wrong. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Continue is only enabled on step 0 once email is verified
  const continueDisabled = step === 0 && otpState !== 'verified';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <BrandLogo size={24} />
          <span className="font-bold text-gray-900">TrainAxis</span>
        </div>
        <Link to="/" className="text-sm text-gray-500 hover:text-gray-900">← Back to home</Link>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">

          {/* Step indicator */}
          <div className="signup-stepper flex items-center gap-2 mb-8 justify-center">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  i < step   ? 'bg-green-500 text-white' :
                  i === step ? 'bg-primary text-white'  :
                               'bg-gray-200 text-gray-400'
                }`}>
                  {i < step ? <FaCheckCircle /> : i + 1}
                </div>
                <span className={`text-sm hidden sm:block ${i === step ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>{label}</span>
                {i < STEPS.length - 1 && <div className={`w-8 h-0.5 ${i < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {step === 0 ? 'Create your account' : step === 1 ? 'Set up your club' : 'Choose your plan'}
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              {step === 0 ? "You'll be the admin of your club." :
               step === 1 ? "Pick a URL for your club's sign-in page." :
               'Start free for 14 days. No credit card needed.'}
            </p>

            {/* ── Step 0: About You ── */}
            {step === 0 && (
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                  <input value={name} onChange={e => setName(e.target.value)}
                    placeholder="Anurag Thapa"
                    className="w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors" />
                </div>

                {/* Email + OTP verification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <div className="flex gap-2">
                    <input
                      value={email}
                      onChange={handleEmailChange}
                      type="email"
                      placeholder="anurag@yourdojo.com"
                      disabled={otpState === 'verified'}
                      className={`flex-1 border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors ${
                        emailAvailable === false    ? 'border-red-400' :
                        otpState === 'verified'     ? 'border-green-400 bg-green-50 text-gray-500' :
                                                     'focus:border-primary'
                      }`}
                    />
                    {/* Send / Resend button — shown while not verified */}
                    {showOtpSection && otpState !== 'verified' && (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={otpState === 'sending' || otpState === 'verifying' || (otpState === 'sent' && countdown > 0)}
                        className="flex-shrink-0 flex items-center gap-1.5 px-4 py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        <MdEmail className="text-base" />
                        {otpState === 'sending' ? 'Sending…' :
                         (otpState === 'sent' && countdown > 0) ? `Resend (${countdown}s)` :
                         otpState === 'sent' ? 'Resend' :
                         'Send Code'}
                      </button>
                    )}
                    {/* Verified badge */}
                    {otpState === 'verified' && (
                      <div className="flex-shrink-0 flex items-center gap-1.5 px-4 py-3 bg-green-50 border border-green-400 text-green-700 text-sm font-semibold rounded-xl">
                        <MdCheckCircle className="text-base" />
                        Verified
                      </div>
                    )}
                  </div>

                  {emailAvailable === false && (
                    <p className="text-red-500 text-xs mt-1">
                      Email already registered. <Link to="/login" className="underline font-medium">Sign in instead</Link>
                    </p>
                  )}

                  {/* OTP input — shown after code is sent and not yet verified */}
                  {(otpState === 'sent' || otpState === 'verifying') && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-2">
                        A 6-digit code was sent to <strong>{email}</strong>. Enter it below.
                      </p>
                      <div className="relative">
                        <input
                          value={otpCode}
                          onChange={handleOtpInput}
                          placeholder="_ _ _ _ _ _"
                          maxLength={6}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          className={`w-full border rounded-xl px-4 py-3 text-center text-2xl font-mono tracking-[0.4em] focus:outline-none transition-colors ${
                            otpError ? 'border-red-400' : 'border-gray-200 focus:border-primary'
                          }`}
                        />
                        {otpState === 'verifying' && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                      {otpError && (
                        <p className="text-red-500 text-xs mt-1">{otpError}</p>
                      )}
                      {!otpError && (
                        <p className="text-gray-400 text-xs mt-1">
                          {countdown > 0
                            ? `Didn't receive it? You can resend in ${countdown}s`
                            : "Didn't receive it? Click Resend above."}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Hint before sending */}
                  {otpState === 'idle' && showOtpSection && (
                    <p className="text-gray-400 text-xs mt-1">We'll send a verification code to this address.</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                  <input
                    value={phone}
                    onChange={e => {
                      // Allow digits, spaces, dashes, and + (only at position 0)
                      const raw = e.target.value;
                      let filtered = raw.replace(/[^\d+\s\-]/g, '');
                      // Strip any + that isn't the first character
                      let hasPlus = filtered.startsWith('+');
                      filtered = filtered.replace(/\+/g, '');
                      if (hasPlus) filtered = '+' + filtered;
                      setPhone(filtered);
                    }}
                    onBlur={() => setPhoneTouched(true)}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="9841234567 or +9779841234567"
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors ${
                      phoneErr ? 'border-red-400 focus:border-red-400' : 'focus:border-primary'
                    }`}
                  />
                  {phoneErr && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <MdError className="text-sm flex-shrink-0" /> {phoneErr}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                  <div className="relative">
                    <input value={password} onChange={e => setPassword(e.target.value)}
                      type={showPass ? 'text' : 'password'} placeholder="At least 8 characters"
                      className="w-full border rounded-xl px-4 py-3 pr-10 text-sm focus:outline-none focus:border-primary transition-colors" />
                    <button type="button" onClick={() => setShowPass(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {showPass ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {password && <PasswordStrength password={password} />}
                </div>
              </div>
            )}

            {/* ── Step 1: Club Info ── */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Club Name</label>
                  <input value={clubName} onChange={e => setClubName(e.target.value)}
                    placeholder="Anurag's Taekwondo Dojo"
                    className="w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Club URL</label>
                  <div className={`flex items-center border rounded-xl overflow-hidden focus-within:border-primary transition-colors ${
                    slugStatus === 'taken' ? 'border-red-400' : slugStatus === 'available' ? 'border-green-400' : ''
                  }`}>
                    <input
                      value={slug}
                      onChange={e => { setSlug(slugify(e.target.value)); setSlugEdited(true); }}
                      placeholder="anurag-dojo"
                      className="flex-1 px-4 py-3 text-sm outline-none"
                    />
                    <span className="px-3 text-gray-400 text-sm bg-gray-50 border-l py-3 whitespace-nowrap">.trainaxis.com</span>
                  </div>
                  {slugStatus === 'checking'  && <p className="text-gray-400 text-xs mt-1">Checking availability…</p>}
                  {slugStatus === 'available' && <p className="text-green-600 text-xs mt-1">✓ Available! Your URL: <strong>{slug}.trainaxis.com</strong></p>}
                  {slugStatus === 'taken'     && <p className="text-red-500  text-xs mt-1">That URL is taken. Try a different one.</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Club Type</label>
                  <select value={clubType} onChange={e => setClubType(e.target.value)}
                    className="w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors">
                    {CLUB_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">City</label>
                    <input value={city} onChange={e => setCity(e.target.value)}
                      placeholder="Kathmandu"
                      className="w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Country</label>
                    <input value="Nepal" readOnly
                      className="w-full border rounded-xl px-4 py-3 text-sm bg-gray-50 text-gray-500 cursor-default" />
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 2: Choose Plan ── */}
            {step === 2 && (
              <div className="space-y-3">
                {[
                  { value: 'free', label: 'Free — 14-day trial',  sub: 'Up to 30 students · All features' },
                  { value: 'pro',  label: 'Pro — NPR 2,499/mo',   sub: 'Unlimited students · Priority support' },
                ].map(p => (
                  <label key={p.value} className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    plan === p.value ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input type="radio" name="plan" value={p.value} checked={plan === p.value}
                      onChange={e => setPlan(e.target.value)} className="mt-1 accent-primary" />
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{p.label}</div>
                      <div className="text-gray-500 text-xs mt-0.5">{p.sub}</div>
                    </div>
                  </label>
                ))}
                <p className="text-xs text-gray-400 text-center mt-2">All plans start with a 14-day free trial. No card needed.</p>
              </div>
            )}

            {/* Error banner */}
            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-600 rounded-xl p-3 text-sm mt-4">
                <MdError className="text-lg flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-6">
              {step > 0 ? (
                <button onClick={() => { setStep(s => s - 1); setError(''); }}
                  className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 text-sm font-medium">
                  <MdArrowBack /> Back
                </button>
              ) : <div />}

              {step < 2 ? (
                <button
                  onClick={nextStep}
                  disabled={continueDisabled}
                  title={continueDisabled ? 'Verify your email to continue' : undefined}
                  className="flex items-center gap-1.5 bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <MdArrowForward />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={loading}
                  className="flex items-center gap-1.5 bg-primary text-white px-8 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50">
                  {loading ? 'Creating your club…' : 'Start Free Trial'} {!loading && <MdArrowForward />}
                </button>
              )}
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 mt-4">
            Already have a club?{' '}
            <Link to="/login" className="text-primary font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
